<template>
  <div class="view pm project">
    <div class="page" v-if="project">
      <a-page-header
        class="rounded bg-gray-1"
        :title="'Project Details'"
        :sub-title="`${project.name ? project.name : ''} ${project.last_name ? project.last_name : ''}`"
        @back="() => $router.push('/pm/projects')"
      >
        <template v-slot:tags>
          <a-tag :color="project.status | PROJECT_STATUS_COLOR_">{{ project.status | PROJECT_STATUS_ }}</a-tag>
        </template>

        <template slot="extra">
          <a-button key="3" @click="onBriefing"> Briefing </a-button>
          <a-button key="2" type="danger"> Delete </a-button>
          <a-button key="1" type="primary" @click="onSave"> Save </a-button>
        </template>

        <a-descriptions :column="2">
          <a-descriptions-item label="Project ID">
            <input class="idbox" :value="project._id" />
          </a-descriptions-item>
          <template v-if="project.customer">
            <a-descriptions-item label="Customer">
              <i class="fe fe-user"></i>
              <router-link :to="`/crm/customer/${project.customer._id}`">
                <span> {{ project.customer.name }}</span>
                <span> {{ project.customer.last_name }}</span>
              </router-link>
            </a-descriptions-item>
            <a-descriptions-item label="Phone" v-if="project.customer.phone">
              <i class="fe fe-phone"></i>
              <span> {{ project.customer.phone }}</span>
              <span v-if="project.customer.phone_extension"> (Ext.{{ project.customer.phone_extension }})</span>
            </a-descriptions-item>
            <a-descriptions-item label="Email" v-if="project.customer.email">
              <i class="fe fe-mail"></i>
              <span> {{ project.customer.email }}</span>
            </a-descriptions-item>
          </template>
          <a-descriptions-item label="Created By" v-if="project.created_by">
            {{ project.created_by.display_name }}
          </a-descriptions-item>
          <a-descriptions-item label="Updated by" v-if="project.updated_by">
            {{ project.updated_by.display_name }}
          </a-descriptions-item>
          <a-descriptions-item label="Created At" v-if="project.created_at">
            <span class="badge badge-primary"> {{ project.created_at | moment('lll') }}</span>
          </a-descriptions-item>
          <a-descriptions-item label="Updated At" v-if="project.updated_at">
            <span class="badge badge-success"> {{ project.updated_at | moment('lll') }}</span>
          </a-descriptions-item>
        </a-descriptions>
      </a-page-header>

      <div class="content">
        <a-tabs v-model="current_tab" defaultActiveKey="1" class="kit-tabs kit-tabs-bold">
          <a-tab-pane :key="1" tab="Basic Info" class="px-2 py-4">
            <!-- form -->
            <a-form-model :model="project" :label-col="labelCol" :wrapper-col="wrapperCol" compact>
              <h5 class="text-black mt-2 mb-3"><strong>Project Basic Information</strong></h5>
              <a-row type="flex">
                <!-- column left -->
                <a-col :span="24" :md="12">
                  <!-- Status -->
                  <a-form-model-item label="Status" prop="status">
                    <a-select v-model="project.status" default-value="pending" :size="size">
                      <a-select-option
                        v-for="option in options_['PROJECT_STATUS']"
                        :key="option.key"
                        :value="option.key"
                      >
                        {{ option.value }}
                      </a-select-option>
                    </a-select>
                  </a-form-model-item>

                  <!-- Property -->
                  <a-form-model-item label="Property">
                    <a-select v-model="project.property" default-value="house" style="width: 100%" :size="size">
                      <a-select-option
                        v-for="option in options_['PROPERTY_TYPES']"
                        :key="option.key"
                        :value="option.key"
                      >
                        {{ option.value }}
                      </a-select-option>
                    </a-select>
                  </a-form-model-item>

                  <!-- Services -->
                  <a-form-model-item label="Services">
                    <a-select
                      v-model="project.services"
                      default-value="interior_renovation"
                      mode="multiple"
                      style="width: 100%"
                      :size="size"
                    >
                      <a-select-option
                        v-for="option in options_['SERVICE_TYPES']"
                        :key="option.key"
                        :value="option.key"
                      >
                        {{ option.locales[locale] }}
                      </a-select-option>
                    </a-select>
                  </a-form-model-item>

                  <!-- Start/End Date -->
                  <a-form-model-item label="Start/End Date">
                    <a-input-group compact>
                      <a-date-picker v-model="project.start_date" :size="size" style="width: 50%" />
                      <a-date-picker v-model="project.end_date" :size="size" style="width: 50%" />
                    </a-input-group>
                  </a-form-model-item>

                  <!-- Team Assignment -->
                  <a-form-model-item label="Team">
                    <a-input-group>
                      <a-select v-model="project.team" default-value="null" style="width: 100%" :size="size">
                        <a-select-option v-for="team in teams" :key="team._id" :value="team._id">
                          {{ team.alias }} - {{ team.name }}
                        </a-select-option>
                      </a-select>
                    </a-input-group>
                  </a-form-model-item>

                  <!-- Insured -->
                  <a-form-model-item label="Insured">
                    <a-radio-group v-model="project.insured" :defaultValue="false" :size="size">
                      <a-radio-button :value="true"> Yes </a-radio-button>
                      <a-radio-button :value="false"> No </a-radio-button>
                    </a-radio-group>
                  </a-form-model-item>

                  <!-- Discount -->
                  <a-form-model-item label="Discount">
                    <a-input-number
                      v-model="project.discount"
                      :default-value="0"
                      :min="0"
                      :max="100"
                      :formatter="(value) => `${value} %`"
                      :parser="(value) => value.replace('%', '')"
                      :size="size"
                    />
                  </a-form-model-item>

                  <!-- Address Search  -->
                  <a-form-model-item label="Search Address">
                    <PlaceAutocompleteInput
                      id="address"
                      class="ant-input ant-input-lg"
                      placeholder="Type to search address"
                      @placechanged="onPlaceChanged"
                    />
                  </a-form-model-item>

                  <!-- Address -->
                  <a-form-model-item label="Full Address">
                    <a-input v-model="project.raw_address" :size="size" />
                  </a-form-model-item>

                  <!-- Location -->
                  <a-form-model-item label="Location">
                    <a-input v-model="project.location" :size="size" />
                  </a-form-model-item>
                  <a-form-model-item label="Appoi. Start/End">
                    <a-input-group>
                      <a-range-picker showTime v-model="appointment_time" :size="size" />
                      <a-button type="primary" @click="generateAppointmentURL" style="width: 100%"> Generate </a-button>
                      <a
                        v-if="appointment_url"
                        v-bind:href="appointment_url"
                        target="_blank"
                        style="text-decoration: underline; font-size: 18px"
                        >Click Here to add appointment to your google calender</a
                      >
                    </a-input-group>
                  </a-form-model-item>
                </a-col>

                <!-- column right -->
                <a-col :span="24" :md="12">
                  <!-- Specialist -->
                  <a-form-model-item label="Specialist">
                    <a-input-group>
                      <a-select v-model="project.specialist" default-value="null" style="width: 100%" :size="size">
                        <a-select-option v-for="staff in staffs" :key="staff._id" :value="staff._id">
                          {{ staff.display_name }}
                        </a-select-option>
                      </a-select>
                    </a-input-group>
                  </a-form-model-item>

                  <!-- Lost Reason -->
                  <a-form-model-item label="Lost Reason">
                    <a-input
                      v-model="project.lost_reason"
                      :size="size"
                      :disabled="!['abandoned', 'cancelled'].includes(project.status)"
                    />
                  </a-form-model-item>

                  <!-- Source -->
                  <a-form-model-item label="Source">
                    <a-input-group>
                      <a-select v-model="project.source" default-value="unknown" style="width: 100%" :size="size">
                        <a-select-option
                          v-for="option in options_['GENERAL_SOURCES']"
                          :key="option.key"
                          :value="option.key"
                        >
                          {{ option.value }}
                        </a-select-option>
                      </a-select>
                    </a-input-group>
                  </a-form-model-item>

                  <!-- Referer -->
                  <a-form-model-item label="Referer">
                    <a-input v-model="project.referer" :size="size" />
                  </a-form-model-item>

                  <!-- Description -->
                  <a-form-model-item label="Description">
                    <a-input v-model="project.description" type="textarea" :rows="20" :size="size" />
                  </a-form-model-item>

                  <!-- Note -->
                  <a-form-model-item label="Note">
                    <a-input v-model="project.note" type="textarea" :rows="4" :size="size" />
                  </a-form-model-item>
                </a-col>
              </a-row>

              <!-- Actions -->
              <a-form-model-item :wrapper-col="{ span: 14, offset: 4 }">
                <a-button type="primary" @click="onSave"> Update </a-button>
                <a-button style="margin-left: 10px" @click="$router.push('/pm/projects')"> Cancel </a-button>
              </a-form-model-item>
            </a-form-model>
          </a-tab-pane>

          <!-- project.status_histories  -->
          <a-tab-pane :key="2" :tab="`Status Histories (${project.status_histories.length || 0})`">
            <div class="inner" style="padding: 1rem" v-if="project.status_histories">
              <div class="headline"></div>
              <a-list item-layout="horizontal" :data-source="project.status_histories">
                <a-list-item slot="renderItem" slot-scope="item, index">
                  <a-list-item-meta>
                    <span slot="title" style="font-size: 1.2rem">{{ index + 1 }} - {{ item.content }}</span>
                    <a-avatar slot="avatar" size="large" icon="area-chart" />
                    <span slot="description">
                      <strong>Status:</strong> {{ item.status }} <strong class="pl-4">Time:</strong>
                      {{ item.created_at | moment('lll') }} <strong class="pl-4">Staff:</strong>
                      {{ item.user }}
                    </span>
                  </a-list-item-meta>
                </a-list-item>
              </a-list>
            </div>
          </a-tab-pane>

          <!-- CONTRACT -->
          <a-tab-pane :key="3" :tab="`Contracts (${project.contracts.length || 0})`">
            <div class="inner">
              <a-page-header title="Contracts" sub-title="All Contracts">
                <template slot="extra">
                  <a-button type="primary" size="small" @click="onNewContract"> New Contract </a-button>
                </template>
              </a-page-header>
              <div class="list">
                <ContractsListTable :contracts="contracts" :loading="loading_contracts" />
              </div>
            </div>
          </a-tab-pane>

          <!-- TASK -->
          <a-tab-pane :key="4" :tab="`Tasks (${project.tasks.length || 0})`">
            <div class="inner">
              <a-page-header title="Tasks" sub-title="All Tasks List">
                <template slot="extra">
                  <a-button type="primary" size="small" @click="onNewTask"> New Task </a-button>
                </template>
              </a-page-header>
              <div class="list">
                <TasksListTable :tasks="tasks" :loading="loading_tasks"></TasksListTable>
              </div>
            </div>
          </a-tab-pane>

          <!-- HISTORY -->
          <a-tab-pane :key="5" :tab="`History (${project.histories.length || 0})`">
            <div class="card p-3 mt-3">
              <div class="input mb-2">
                <a-input-search
                  placeholder="Type what happened here..."
                  enter-button="Submit"
                  size="large"
                  @search="onSubmitHistory"
                />
              </div>
              <div class="histories">
                <a-list v-if="histories && histories.length > 0" item-layout="horizontal" :data-source="histories">
                  <a-list-item slot="renderItem" slot-scope="history, index">
                    <a-list-item-meta :description="history.content">
                      <span slot="title"> {{ history.created_at | moment('lll') }}</span>
                      <a-avatar slot="avatar" :size="45" shape="square"> {{ index + 1 }}</a-avatar>
                    </a-list-item-meta>
                  </a-list-item>
                </a-list>
                <a-empty v-else />
              </div>
            </div>
          </a-tab-pane>

          <a-tab-pane :key="6" tab="Financial Record">
            <div class="card p-3 mt-3">
              <label for="rate" class="font-size-16">Estimated Price</label>
              <a-input-number
                size="large"
                style="width: 300px"
                v-model="project.estimated_price"
                :default-value="0"
                :formatter="(value) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')"
                :parser="(value) => value.replace(/\$\s?|(,*)/g, '')"
              />
              <label for="rate" class="font-size-16 pt-3">Additional Charge</label>
              <a-input-number
                size="large"
                style="width: 300px"
                v-model="project.additional_charge"
                :default-value="0"
                :formatter="(value) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')"
                :parser="(value) => value.replace(/\$\s?|(,*)/g, '')"
              />
              <label for="rate" class="font-size-16 pt-3">Total Cost</label>
              <a-input-number
                size="large"
                style="width: 300px"
                v-model="project.total_cost"
                :default-value="0"
                :formatter="(value) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')"
                :parser="(value) => value.replace(/\$\s?|(,*)/g, '')"
              />
              <label for="rate" class="font-size-16 pt-3">Invoice Status</label>
              <a-select v-model="project.invoice_status" size="large" default-value="pending" style="width: 300px">
                <a-select-option value="pending"> Pending </a-select-option>
                <a-select-option value="sent"> Sent </a-select-option>
                <a-select-option value="paid"> Paid </a-select-option>
                <a-select-option value="overdue"> Overdue </a-select-option>
                <a-select-option value="void"> Void </a-select-option>
                <a-select-option value="write-off"> Write Off </a-select-option>
                <a-select-option value="draft"> Draft </a-select-option>
              </a-select>
              <label for="rate" class="font-size-16 pt-3">Invoice Number</label>
              <a-input
                v-model="project.invoice_number"
                addon-before="NO."
                default-value=""
                size="large"
                style="width: 300px"
              />
              <label for="rate" class="font-size-16 pt-3">Payment Method</label>
              <a-select v-model="project.payment_method" size="large" default-value="cash" style="width: 300px">
                <a-select-option value="cash"> Cash </a-select-option>
                <a-select-option value="debit"> Debit </a-select-option>
                <a-select-option value="credit"> Credit </a-select-option>
                <a-select-option value="e-transfer"> e-Transfer(EMT) </a-select-option>
                <a-select-option value="wechat-pay"> WeChat </a-select-option>
                <a-select-option value="alipay"> Alipay </a-select-option>
                <a-select-option value="bank"> Bank </a-select-option>
                <a-select-option value="other"> Other </a-select-option>
              </a-select>
              <a-button class="mt-3" type="primary" @click="onSave" style="width: 300px"> Update </a-button>
            </div>
          </a-tab-pane>
          <a-tab-pane :key="7" tab="Customer Feedback">
            <div class="card p-3 mt-3">
              <div class="d-flex flex-column" style="max-width: 550px">
                <label class="font-size-16" for="rate">Rating</label>
                <a-rate v-model="project.customer_ratings" :default-value="4.5" allow-half size="large" />
                <label class="font-size-16 pt-3" for="rate">Nickname</label>
                <a-input v-model="project.customer_nickname" placeholder="Customer Nickname" size="large" />
                <label class="font-size-16 pt-3" for="rate">Testimonial</label>
                <a-textarea
                  v-model="project.customer_feedback"
                  placeholder="What does customer say?"
                  :rows="10"
                  size="large"
                />
                <a-button class="mt-3" type="primary" @click="onSave"> Save </a-button>
              </div>
            </div>
          </a-tab-pane>
        </a-tabs>
      </div>
    </div>
  </div>
</template>

<script>
import store from '@/store'
import { ContractsListTable, TasksListTable } from '@/components/reno/tables'
import { PlaceAutocompleteInput } from '@/components/reno/inputs'
import * as staffsService from '@/services/system/staffs'
import * as teamsService from '@/services/pm/teams'

const KEY = 'project'
const KEY2 = 'project_tasks'
const KEY3 = 'contracts'
const KEY4 = 'histories'

export default {
  components: {
    ContractsListTable,
    TasksListTable,
    PlaceAutocompleteInput,
  },
  data() {
    return {
      appointment_time: '',
      appointment_url: '',
      current_tab: 1,
      size: 'large',
      labelCol: { xs: 24, sm: 5 },
      wrapperCol: { xs: 24, sm: 19 },
      staffs: [],
      teams: [],
    }
  },
  watch: {
    async current_tab(key) {
      if (key === 3) {
        this.$store.dispatch('pm/fetchContracts')
      }
      if (key === 4) {
        await this.fetchTasks()
      }
      if (key === 5) {
        this.$store.dispatch('pm/fetchHistory')
      }
    },
  },
  computed: {
    locale() {
      return this.$i18n.locale ? (this.$i18n.locale === 'zh-CN' ? 'zh' : 'en') : 'en'
    },
    project: {
      get() {
        return this.$store.getters['pm/data'](KEY)
      },
      set(project) {
        this.$store.commit('pm/UPDATE', { key: KEY, data: project })
      },
    },
    tasks: () => store.getters['pm/data'](KEY2),
    loading_tasks: () => store.getters['pm/loading'](KEY2) || null,
    contracts: () => store.getters['pm/data'](KEY3) || null,
    loading_contracts: () => store.getters['pm/loading'](KEY3) || null,
    histories: () => store.getters['pm/data'](KEY4) || null,
  },
  methods: {
    async fetch() {
      if (this.$route.params && this.$route.params.id) {
        await store.dispatch('pm/fetchProject', this.$route.params.id)
      }
    },
    async fetchTasks() {
      await this.$store.dispatch('pm/fetchTasksByProject')
    },
    async onBriefing() {
      this.$store.dispatch('pm/open', 'BRIEFING')
    },
    async onNewContract() {
      this.$store.dispatch('pm/open', 'CREATE_CONTRACT')
    },
    async onNewTask() {
      this.$store.dispatch('pm/open', 'CREATE_TASK')
    },
    onPlaceChanged(place) {
      if (place) {
        // console.log(place)
        if (place.formatted_address) this.project.raw_address = place.formatted_address
        if (place.locality && place.administrative_area_level_1)
          this.project.location = `${place.locality},${place.administrative_area_level_1}`
        this.project = Object.assign({}, this.project)
      }
    },
    onSubmitHistory(content) {
      if (content && content !== '') {
        this.$store.dispatch('pm/insertHistory', content)
      }
    },
    async onSave() {
      if (this.project) {
        const project = await store.dispatch('pm/updateProject', this.project)

        if (project) {
          this.project = project
          this.$message.success(`Project ${project.id} has been updated successfully!`)
        } else {
          this.$message.error('Oops, The project update failed!')
        }
      }
    },
    generateAppointmentURL() {
      const start = new Date(this.appointment_time[0]).toISOString().replaceAll('-', '').replaceAll(':', '')
      const end = new Date(this.appointment_time[1]).toISOString().replaceAll('-', '').replaceAll(':', '')
      this.appointment_url = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=Appointment with ${this.project.customer.name}&dates=${start}/${end}&details=${this.project.description}&location=${this.project.raw_address}`
    },
  },
  async created() {
    await this.fetch()
    this.staffs = await staffsService.fetchAllStaffs()
    this.teams = await teamsService.fetchTeams()
  },
  mounted() {
    if (this.$route.query) {
      if (this.$route.query.tab === 'task') {
        this.current_tab = 3
      }
      if (this.$route.query.task === 'on') {
        this.onNewTask()
      }
    }
  },
}
</script>

<style lang="scss" scoped>
.ant-tabs-tabpane {
  border-top: 1px solid #dee2e6;
}
</style>
